import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';

const ACTION_CONFIG = {
  item_created:     { label: 'Item Created',     color: '#10b981', bg: '#ecfdf5' },
  item_updated:     { label: 'Item Updated',     color: '#3b82f6', bg: '#eff6ff' },
  item_deactivated: { label: 'Item Deactivated', color: '#ef4444', bg: '#fef2f2' },
  doc_uploaded:     { label: 'Doc Uploaded',      color: '#8b5cf6', bg: '#f5f3ff' },
  doc_deleted:      { label: 'Doc Deleted',       color: '#d97706', bg: '#fffbeb' },
  template_applied: { label: 'Template Applied',  color: '#0891b2', bg: '#ecfeff' },
};

export default function AuditLogPage() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const PAGE_SIZE = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (filterAction) filters.action = filterAction;
      if (filterEntity) filters.entity_type = filterEntity;
      const data = await api.getAuditLog(filters);
      setEntries(Array.isArray(data.entries) ? data.entries : []);
      setTotal(data.total || 0);
    } catch {
      setEntries([]);
    }
    setLoading(false);
  }, [page, filterAction, filterEntity]);

  useEffect(() => { load(); }, [load]);

  function formatTime(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'Z');
    return d.toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <h1>Audit Log</h1>
        <span style={{ fontSize: '13px', color: '#64748b' }}>{total} total entries</span>
      </div>

      <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '16px' }}>
        All compliance-related actions are logged here for audit purposes.
      </p>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <select value={filterAction} onChange={e => { setFilterAction(e.target.value); setPage(0); }} className="form-input" style={{ maxWidth: '180px' }}>
          <option value="">All Actions</option>
          {Object.entries(ACTION_CONFIG).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select value={filterEntity} onChange={e => { setFilterEntity(e.target.value); setPage(0); }} className="form-input" style={{ maxWidth: '160px' }}>
          <option value="">All Types</option>
          <option value="employee">Employee</option>
          <option value="company">Company</option>
          <option value="facility">Facility</option>
          <option value="truck">Truck</option>
        </select>
      </div>

      {loading ? (
        <div className="loading-spinner">Loading audit log...</div>
      ) : entries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
          No audit entries found.
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Entity</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {entries.map(entry => {
                  const ac = ACTION_CONFIG[entry.action] || { label: entry.action, color: '#64748b', bg: '#f1f5f9' };
                  return (
                    <tr key={entry.id}>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '12px', color: '#64748b' }}>
                        {formatTime(entry.created_at)}
                      </td>
                      <td style={{ fontSize: '13px', fontWeight: 600 }}>{entry.user_name || 'System'}</td>
                      <td>
                        <span style={{
                          padding: '3px 10px', borderRadius: '999px',
                          fontSize: '11px', fontWeight: 700,
                          background: ac.bg, color: ac.color,
                        }}>
                          {ac.label}
                        </span>
                      </td>
                      <td>
                        {entry.entity_type && (
                          <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8' }}>
                            {entry.entity_type}
                          </span>
                        )}
                      </td>
                      <td style={{ fontSize: '13px', color: '#475569', maxWidth: '400px' }}>
                        {entry.details || '\u2014'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
              <button className="btn btn-sm btn-outline" onClick={() => setPage(p => p - 1)} disabled={page === 0}>
                Previous
              </button>
              <span style={{ fontSize: '13px', color: '#64748b', padding: '6px 12px' }}>
                Page {page + 1} of {totalPages}
              </span>
              <button className="btn btn-sm btn-outline" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
